import { useParams } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { VersionNavigation } from "@/components/VersionNavigation";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

interface IdenticalTranslation {
    id: number;
    parent_id: number;
    original_text: string;
    translated_text: string;
    original_name: string;
    original_language: string;
    translated_language: string;
    created_at: string;
    updated_at: string;
}

const Gomb11Page = () => {
    const { gameId, versionId } = useParams();
    const [currentPage, setCurrentPage] = useState(1);

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ["identicalTranslations", versionId, currentPage],
        queryFn: async () => {
            if (!versionId) return { identical_translations: [], count: 0, page: 1, per_page: 50, has_more: false };

            const response = await fetch(
                `/api/extracted_texts/identical_translations?version_id=${versionId}&page=${currentPage}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch identical translations");
            }

            return response.json();
        },
        enabled: !!versionId,
    });

    const identicalTranslations = data?.identical_translations || [];
    const count = data?.count || 0;
    const page = data?.page || 1;
    const perPage = data?.per_page || 50;
    const hasMore = data?.has_more || false;

    return (
        <div className="w-full mx-auto py-6 px-4">
            <Breadcrumb className="mb-6">
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <BreadcrumbLink href="/games">Játékok</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink href={`/games/${gameId}`}>{gameId}</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink href={`/games/${gameId}/versions/${versionId}`}>{versionId}</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <span>Azonos</span>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="mb-6">
                <VersionNavigation gameId={gameId} versionId={versionId} />
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>
                        Azonos fordítások (Oldal: {page}, Ezen az oldalon: {count})
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {isLoading ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <p>Betöltés...</p>
                        </div>
                    ) : isError ? (
                        <div className="text-center py-8 text-red-600">
                            <p>Hiba: {error?.message || "Ismeretlen hiba"}</p>
                        </div>
                    ) : identicalTranslations.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <p>Nincs olyan fordítás, ahol az eredeti szöveg és a fordítás 100%-ban megegyeznek.</p>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Eredeti szöveg</TableHead>
                                            <TableHead>Fordítás</TableHead>
                                            <TableHead>Eredeti név</TableHead>
                                            <TableHead>Dátum</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {identicalTranslations.map((item: IdenticalTranslation) => (
                                            <TableRow key={item.id}>
                                                <TableCell className="max-w-xs truncate" title={item.original_text}>
                                                    {item.original_text}
                                                </TableCell>
                                                <TableCell className="max-w-xs truncate" title={item.translated_text}>
                                                    {item.translated_text}
                                                </TableCell>
                                                <TableCell className="max-w-xs truncate" title={item.original_name}>
                                                    {item.original_name}
                                                </TableCell>
                                                <TableCell>
                                                    {new Date(item.created_at).toLocaleDateString('hu-HU')}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Pagination controls */}
                            <div className="flex gap-2 justify-center items-center mt-6">
                                <Button
                                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                    disabled={currentPage === 1 || isLoading}
                                    size="sm"
                                >
                                    ← Előző
                                </Button>

                                <span className="text-sm text-muted-foreground px-4">
                                    Oldal {page}
                                </span>

                                <Button
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={!hasMore || isLoading}
                                    size="sm"
                                >
                                    Következő →
                                </Button>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default Gomb11Page;
